import { useMemo, useState } from "react";
import {
  ApiError,
  useProjectBuilds,
  useRefreshBuild,
  useSandbox,
  useStartBuild,
  type EasBuildPlatform,
  type EasBuildRecord,
} from "@vorloop/api-client-react";
import { QrPlaceholder } from "./QrPlaceholder";

export function ExpoPreviewPanel({ projectId }: { projectId: string }) {
  const deepLink = `vor://preview/${projectId}`;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, fontSize: 12 }}>
      <p style={{ color: "var(--text-secondary)", margin: 0, lineHeight: 1.5 }}>
        Test on a mobile device to experience touch gestures and native features.
        <strong> No Expo Go required</strong> — Vor opens your project in the same app you sign in to.
      </p>

      <Step number={1} title="Install Vor on your phone" hint="One app handles both editing and preview">
        <div style={{ display: "flex", gap: 8 }}>
          <StoreButton platform="ios" />
          <StoreButton platform="android" />
        </div>
      </Step>

      <Step number={2} title="Scan the QR code" hint="Your camera opens the Vor app directly to this project's preview">
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <QrPlaceholder size={140} value={deepLink} />
          <code
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--text-tertiary)",
              padding: "4px 6px",
              background: "var(--bg-panel-2)",
              border: "1px solid var(--border-default)",
              borderRadius: 6,
              wordBreak: "break-all",
            }}
            title="Vor deep link — opens directly in the Vor mobile app."
          >
            {deepLink}
          </code>
          <div style={{ color: "var(--text-tertiary)", fontSize: 10 }}>
            Web previews render now. Embedded Expo dev client for mobile projects lands in Phase 1.1.
          </div>
        </div>
      </Step>

      <div
        style={{
          padding: 10,
          background: "var(--bg-panel-2)",
          border: "1px solid var(--border-default)",
          borderRadius: 8,
          color: "var(--text-secondary)",
          display: "flex",
          gap: 8,
          alignItems: "flex-start",
        }}
      >
        <span aria-hidden style={{ color: "var(--accent-amber)" }}>💡</span>
        <span>
          Pull down inside the preview to refresh. Shake the phone any time to open the Vor dev menu.
        </span>
      </div>

      <div style={{ flex: 1 }} />

      <EasBuildSection projectId={projectId} />
    </div>
  );
}

function EasBuildSection({ projectId }: { projectId: string }) {
  const sandbox = useSandbox(projectId);
  const sandboxRunning =
    sandbox.data?.sandbox?.status === "running" ||
    sandbox.data?.sandbox?.status === "starting";
  const builds = useProjectBuilds(projectId);
  const startBuild = useStartBuild(projectId);
  const refreshBuild = useRefreshBuild(projectId);

  // Pick the most recent build per platform for the card display.
  // History below the cards shows the full ordered list.
  const latestPerPlatform = useMemo(() => {
    const map = new Map<string, EasBuildRecord>();
    for (const b of builds.data?.items ?? []) {
      if (!map.has(b.platform)) map.set(b.platform, b);
    }
    return map;
  }, [builds.data]);

  const startError = startBuildErrorMessage(startBuild.error);

  return (
    <div
      style={{
        padding: 12,
        background: "var(--bg-panel-2)",
        border: "1px solid var(--border-default)",
        borderRadius: 10,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div style={{ fontWeight: 600, color: "var(--text-primary)", fontSize: 13 }}>
        Build for App Store / Play Store
      </div>
      <div style={{ color: "var(--text-tertiary)", fontSize: 12 }}>
        EAS Build packages your project as a signed iOS / Android binary, ready for store submission.
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <EasBuildCard
          platform="ios"
          latest={latestPerPlatform.get("ios")}
          disabled={!sandboxRunning || startBuild.isPending}
          onStart={() => startBuild.mutate({ platform: "ios" })}
          onRefresh={(id) => refreshBuild.mutate(id)}
        />
        <EasBuildCard
          platform="android"
          latest={latestPerPlatform.get("android")}
          disabled={!sandboxRunning || startBuild.isPending}
          onStart={() => startBuild.mutate({ platform: "android" })}
          onRefresh={(id) => refreshBuild.mutate(id)}
        />
      </div>
      {!sandboxRunning && (
        <div
          style={{
            color: "var(--text-tertiary)",
            fontSize: 11,
            padding: "6px 10px",
            background: "var(--bg-canvas)",
            border: "1px dashed var(--border-default)",
            borderRadius: 6,
          }}
        >
          Start a sandbox from the top bar — the build runs inside it via <code>eas build</code>.
        </div>
      )}
      {startError && (
        <div
          role="alert"
          style={{
            fontSize: 11,
            padding: "6px 10px",
            background: "rgba(248,113,113,0.12)",
            border: "1px solid rgba(248,113,113,0.35)",
            borderRadius: 6,
            color: "var(--accent-red)",
          }}
        >
          {startError}
        </div>
      )}
      {(builds.data?.items.length ?? 0) > 0 && (
        <BuildHistoryList
          items={builds.data?.items ?? []}
          onRefresh={(id) => refreshBuild.mutate(id)}
        />
      )}
    </div>
  );
}

function startBuildErrorMessage(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    const p = err.problem as { detail?: string; title?: string };
    return p.detail ?? p.title ?? "Failed to start build";
  }
  return (err as Error).message ?? "Failed to start build";
}

const STATUS_COLOR: Record<string, string> = {
  new: "#a5b4fc",
  "in-queue": "#fbbf24",
  "in-progress": "#a5b4fc",
  errored: "#fca5a5",
  finished: "#34d399",
  canceled: "var(--text-tertiary)",
};

function statusColor(status: string): string {
  return STATUS_COLOR[status] ?? "var(--text-tertiary)";
}

function EasBuildCard({
  platform,
  latest,
  disabled,
  onStart,
  onRefresh,
}: {
  platform: EasBuildPlatform;
  latest: EasBuildRecord | undefined;
  disabled: boolean;
  onStart: () => void;
  onRefresh: (id: string) => void;
}) {
  const label = platform === "ios" ? "iOS" : "Android";
  const status = latest?.status ?? "idle";
  const inFlight = status === "new" || status === "in-queue" || status === "in-progress";
  const canStart = !disabled && !inFlight;
  return (
    <div
      style={{
        flex: 1,
        padding: 10,
        background: "var(--bg-canvas)",
        border: "1px solid var(--border-default)",
        borderRadius: 8,
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontWeight: 500, fontSize: 12 }}>{label}</span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: statusColor(status),
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          {status}
        </span>
      </div>
      <button
        type="button"
        onClick={onStart}
        disabled={!canStart}
        data-testid={`eas-build-start-${platform}`}
        style={{
          padding: "5px 8px",
          background: canStart ? "var(--accent-blue)" : "var(--bg-active)",
          color: canStart ? "#fff" : "var(--text-tertiary)",
          border: "none",
          borderRadius: 6,
          fontSize: 11,
          fontWeight: 600,
          cursor: canStart ? "pointer" : "not-allowed",
        }}
      >
        {status === "finished"
          ? "Rebuild"
          : status === "errored"
            ? "Retry"
            : status === "idle"
              ? "Start build"
              : "In progress…"}
      </button>
      {latest && latest.status === "finished" && latest.artifactUrl && (
        <a
          href={latest.artifactUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            padding: "4px 8px",
            background: "transparent",
            color: "var(--accent-blue)",
            border: "1px solid var(--accent-blue)",
            borderRadius: 6,
            fontSize: 10,
            textAlign: "center",
            textDecoration: "none",
          }}
        >
          Download {platform === "ios" ? "IPA" : "APK"}
        </a>
      )}
      {latest && inFlight && (
        <button
          type="button"
          onClick={() => onRefresh(latest.id)}
          style={{
            padding: "4px 8px",
            background: "transparent",
            color: "var(--text-secondary)",
            border: "1px solid var(--border-default)",
            borderRadius: 6,
            fontSize: 10,
            cursor: "pointer",
          }}
        >
          Refresh status
        </button>
      )}
    </div>
  );
}

function BuildHistoryList({
  items,
  onRefresh,
}: {
  items: ReadonlyArray<EasBuildRecord>;
  onRefresh: (id: string) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: "var(--text-tertiary)",
        }}
      >
        Recent builds ({items.length})
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 2 }}>
        {items.slice(0, 8).map((b) => (
          <li
            key={b.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "5px 8px",
              background: "var(--bg-canvas)",
              border: "1px solid var(--border-default)",
              borderRadius: 6,
              fontSize: 11,
            }}
          >
            <span style={{ width: 56, color: "var(--text-secondary)" }}>{b.platform}</span>
            <span style={{ width: 88, color: statusColor(b.status), textTransform: "uppercase", letterSpacing: 0.3, fontSize: 10 }}>
              {b.status}
            </span>
            <span style={{ flex: 1, color: "var(--text-tertiary)", fontFamily: "var(--font-mono)", fontSize: 10, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {new Date(b.createdAt).toLocaleString()}
            </span>
            {b.artifactUrl ? (
              <a
                href={b.artifactUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "var(--accent-blue)", fontSize: 10, textDecoration: "none" }}
              >
                Download
              </a>
            ) : (
              <button
                type="button"
                onClick={() => onRefresh(b.id)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--text-tertiary)",
                  cursor: "pointer",
                  fontSize: 10,
                  padding: 0,
                }}
              >
                Refresh
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Step({
  number,
  title,
  hint,
  children,
}: {
  number: number;
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <div
        aria-hidden
        style={{
          width: 22,
          height: 22,
          borderRadius: 11,
          border: "1px solid var(--border-strong)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          color: "var(--text-secondary)",
          flexShrink: 0,
        }}
      >
        {number}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
        <div>
          <div style={{ fontWeight: 600, color: "var(--text-primary)", fontSize: 13 }}>{title}</div>
          <div style={{ color: "var(--text-tertiary)", fontSize: 12 }}>{hint}</div>
        </div>
        {children}
      </div>
    </div>
  );
}

function StoreButton({ platform }: { platform: "ios" | "android" }) {
  return (
    <button
      type="button"
      style={{
        flex: 1,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        background: "var(--bg-canvas)",
        border: "1px solid var(--border-default)",
        color: "var(--text-secondary)",
        borderRadius: 8,
        fontSize: 12,
        cursor: "pointer",
        justifyContent: "center",
      }}
    >
      {platform === "ios" ? (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16.5 12.5c0-3 2.4-4.4 2.5-4.4-1.4-2-3.5-2.3-4.2-2.3-1.8-.2-3.5 1.1-4.4 1.1-.9 0-2.3-1-3.8-1-2 0-3.8 1.2-4.8 3-2 3.5-.5 8.7 1.5 11.5 1 1.4 2.1 3 3.6 2.9 1.5-.1 2-.9 3.8-.9s2.3.9 3.8.9c1.6 0 2.6-1.4 3.6-2.8 1.1-1.6 1.6-3.2 1.6-3.3 0 0-3.2-1.2-3.2-4.7zM13.9 4.1c.8-1 1.4-2.4 1.2-3.8-1.2 0-2.6.8-3.4 1.8-.8.9-1.5 2.3-1.3 3.7 1.3.1 2.7-.7 3.5-1.7z" />
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17 8H7l-2 4 2 9h10l2-9-2-4Zm-7 8h-1v-3h1v3Zm5 0h-1v-3h1v3ZM10 3.5l-.7-1.3a.4.4 0 0 1 .7-.4l1 1.8m4-1.8a.4.4 0 0 1 .7.4L14 5.4" />
        </svg>
      )}
      {platform === "ios" ? "iOS" : "Android"}
    </button>
  );
}
